/**
 * useUpdateSchemaDescription — PUT /enterprise/connectors/{conn_id}/schema/{object_name}.
 *
 * V1 ships a SINGLE endpoint that accepts the table description AND a
 * ``column_descriptions`` map in one body — we mirror that exactly, no
 * FE-side splitting. The mutation does an optimistic update on the
 * ``useSchema`` cache: the user sees their edit instantly, rollback on
 * BE failure restores the previous text + the caller surfaces the error.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  type SchemaResponse,
  type SchemaUpdateRequest,
  type SchemaUpdateResponse,
  updateEnterpriseSchemaDescription,
} from "@ando/core/api";

import { enterpriseSchemaKey } from "./enterpriseKeys";

export interface UseUpdateSchemaDescriptionVars {
  readonly connId: string;
  readonly objectName: string;
  readonly body: SchemaUpdateRequest;
}

export function useUpdateSchemaDescription() {
  const qc = useQueryClient();
  return useMutation<
    SchemaUpdateResponse,
    Error,
    UseUpdateSchemaDescriptionVars,
    { previous: SchemaResponse | undefined }
  >({
    mutationFn: ({ connId, objectName, body }) =>
      updateEnterpriseSchemaDescription(connId, objectName, body),
    onMutate: async (vars) => {
      const key = enterpriseSchemaKey(vars.connId);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<SchemaResponse>(key);
      if (previous) {
        qc.setQueryData<SchemaResponse>(key, {
          ...previous,
          tables: previous.tables.map((t) =>
            t.name === vars.objectName
              ? {
                  ...t,
                  description: vars.body.description,
                  columns: t.columns.map((c) => {
                    const overrides = vars.body.column_descriptions;
                    if (overrides && c.name in overrides) {
                      return { ...c, description: overrides[c.name] ?? c.description };
                    }
                    return c;
                  }),
                }
              : t,
          ),
        });
      }
      return { previous };
    },
    onError: (_err, vars, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(enterpriseSchemaKey(vars.connId), ctx.previous);
      }
    },
    onSettled: (_data, _err, vars) => {
      void qc.invalidateQueries({ queryKey: enterpriseSchemaKey(vars.connId) });
    },
  });
}
